import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, CircularProgress, Alert, IconButton,
    Tooltip, TextField, MenuItem, Select, FormControl, InputLabel,
    Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../services/api';

const CANAL_LABEL = { EMAIL: 'E-mail', WHATSAPP: 'WhatsApp' };
const TIPO_LABEL = {
    ACEITE_DOCUMENTO: 'Aceite',
    CONTRATO: 'Contrato',
    RENOVACAO_ASSINATURA: 'Renovação',
    GENERICO: 'Genérico',
};
const STATUS_COLOR = { ENVIADA: 'success', PENDENTE: 'warning', FALHA: 'error' };

function DetalheRow({ row }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ width: 40, py: 0.5 }}>
                    <IconButton size="small" onClick={() => setOpen(o => !o)}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>{row.id}</TableCell>
                <TableCell sx={{ py: 0.5 }}>{CANAL_LABEL[row.canal] ?? row.canal}</TableCell>
                <TableCell sx={{ py: 0.5 }}>{TIPO_LABEL[row.tipo] ?? row.tipo}</TableCell>
                <TableCell sx={{ py: 0.5 }}>
                    <Chip size="small" label={row.status} color={STATUS_COLOR[row.status] ?? 'default'} />
                </TableCell>
                <TableCell sx={{ py: 0.5, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={row.destinatario}><span>{row.destinatario}</span></Tooltip>
                </TableCell>
                <TableCell sx={{ py: 0.5, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.assunto}
                </TableCell>
                <TableCell sx={{ py: 0.5, fontSize: 12, color: 'text.secondary' }}>
                    {row.criadoEm ? new Date(row.criadoEm).toLocaleString('pt-BR') : '—'}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 0, px: 2 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1.5 }}>
                            {row.conteudo && (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                    {row.conteudo}
                                </Typography>
                            )}
                            {row.mensagemErro && (
                                <Alert severity="error" sx={{ mt: 1 }}>{row.mensagemErro}</Alert>
                            )}
                            {row.tipoReferencia && (
                                <Typography variant="caption" color="text.secondary">
                                    Referência: {row.tipoReferencia} #{row.idReferencia}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function HistoricoNotificacoes() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filtroCanal, setFiltroCanal] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [filtroBusca, setFiltroBusca] = useState('');

    const carregar = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/notificacoes');
            setRows(res.data ?? []);
        } catch {
            setError('Erro ao carregar histórico de notificações.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const filtradas = rows.filter(r => {
        if (filtroCanal && r.canal !== filtroCanal) return false;
        if (filtroStatus && r.status !== filtroStatus) return false;
        if (filtroBusca) {
            const q = filtroBusca.toLowerCase();
            return (r.destinatario ?? '').toLowerCase().includes(q)
                || (r.assunto ?? '').toLowerCase().includes(q);
        }
        return true;
    });

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Histórico de Notificações
                </Typography>
                <Tooltip title="Atualizar">
                    <IconButton onClick={carregar} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField size="small" label="Buscar destinatário / assunto"
                    value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)}
                    sx={{ flex: '1 1 200px' }} />
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Canal</InputLabel>
                    <Select label="Canal" value={filtroCanal} onChange={e => setFiltroCanal(e.target.value)}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="EMAIL">E-mail</MenuItem>
                        <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="ENVIADA">Enviada</MenuItem>
                        <MenuItem value="PENDENTE">Pendente</MenuItem>
                        <MenuItem value="FALHA">Falha</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#1ABB9C' }} />
                </Box>
            ) : (
                <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell />
                                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Canal</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Destinatário</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Assunto</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtradas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            Nenhuma notificação encontrada.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtradas.map(row => <DetalheRow key={row.id} row={row} />)
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" sx={{ p: 2, display: 'block', color: 'text.secondary' }}>
                        Exibindo {filtradas.length} de {rows.length} registros (últimas 200 notificações)
                    </Typography>
                </Paper>
            )}
        </Box>
    );
}
